// AdditionalItemsComponent.jsx
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
    Dialog,
    Button,
    Card,
    Toast,
    Dropdown,
    InputText,
    InputTextarea,
    DataTable,
    Column,
    Panel,
    Divider,
    Badge,
    Message,
    ProgressSpinner,
    Tag
} from 'primereact';
import useAuth from "hooks/useAuth";

const API_URL = process.env.REACT_APP_API_PATH;

const AdditionalItemsComponent = ({ visible, onHide, onSuccess }) => {
    const auth = useAuth();
    const toast = useRef(null);

    // State
    const [searchOrderId, setSearchOrderId] = useState('');
    const [orderData, setOrderData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [searching, setSearching] = useState(false);
    const [items, setItems] = useState([]);
    const [filteredItemTypes, setFilteredItemTypes] = useState([]);
    const [treatments, setTreatments] = useState([]);
    const [selectedShop, setSelectedShop] = useState(null);
    const [isLoadingTreatments, setIsLoadingTreatments] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [errors, setErrors] = useState({});

    // New item form state
    const [newItem, setNewItem] = useState({
        itemtype: '',
        itemTypeId: null,
        quantity: 1,
        treatment: '',
        unitprice: 0,
        totalprice: 0,
        itemcategory: '',
        instructions: ''
    });

    // Load item types when shop is selected
    const loadItemTypesForShop = async (shopId) => {
        if (!shopId) {
            setFilteredItemTypes([]);
            return;
        }

        try {
            const response = await axios.get(`${API_URL}customer/itemtypes`, {
                params: { partnerShopId: shopId },
                timeout: 10000
            });

            const items = response.data.records || response.data || [];
            setFilteredItemTypes(items);
            console.log(`Loaded ${items.length} item types for shop ${shopId}`);

        } catch (error) {
            console.error('Error loading item types:', error);
            showError('Failed to load items for this shop');
        }
    };

    // Load treatments when shop is selected
    const loadTreatments = async (shopId) => {
        if (!shopId) {
            setTreatments([]);
            return;
        }

        setIsLoadingTreatments(true);

        try {
            const response = await axios.get(`${API_URL}customer/treatments`, {
                params: { partnerShopId: shopId },
                timeout: 10000
            });

            if (response.data.success) {
                const treatmentsData = response.data.records || [];
                setTreatments(treatmentsData);
                console.log(`Loaded ${treatmentsData.length} treatments for shop ${shopId}`);
            } else {
                throw new Error(response.data.message || 'Failed to load treatments');
            }
        } catch (error) {
            console.error('Error loading treatments:', error);
            setTreatments([]);
        } finally {
            setIsLoadingTreatments(false);
        }
    };

    // Search order by ID
    const searchOrder = async () => {
        if (!searchOrderId.trim()) {
            showError('Please enter an Order ID');
            return;
        }

        setSearching(true);
        setOrderData(null);
        setItems([]);

        try {
            const response = await axios.get(
                `${API_URL}bookings/${searchOrderId.trim()}`,
                {
                    headers: {
                        'Authorization': `Bearer ${auth.accessToken}`,
                        'Content-Type': 'application/json'
                    },
                    timeout: 15000
                }
            );

            if (response.data && response.data.success) {
                const order = response.data.data || response.data.booking || response.data;
                setOrderData(order);

                // Set the shop from the order
                const shop = order.PartnerShop || order.partnerShop || order.Shop || order.shop;
                if (shop) {
                    setSelectedShop({
                        Id: shop.Id || shop.id,
                        ShopName: shop.ShopName || shop.shopName || shop.name,
                        Address: shop.Address || shop.address
                    });
                    // Load items and treatments for this shop
                    await loadItemTypesForShop(shop.Id || shop.id);
                    await loadTreatments(shop.Id || shop.id);
                }

                // Set existing items
                const existingItems = order.Items || order.items || order.OrderItems || order.orderItems || [];
                setItems(existingItems);

                showSuccess(`Order #${searchOrderId} found successfully!`);
            } else {
                throw new Error(response.data?.message || 'Order not found');
            }

        } catch (error) {
            console.error('Error searching order:', error);
            if (error.response && error.response.status === 404) {
                showError(`Order #${searchOrderId} not found. Please check the ID and try again.`);
            } else {
                showError(error.response?.data?.message || 'Failed to find order. Please try again.');
            }
        } finally {
            setSearching(false);
        }
    };

    // Handle search on Enter key
    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            searchOrder();
        }
    };

    // Handle new item field changes
    const handleNewItemChange = (field, value) => {
        const updatedItem = { ...newItem, [field]: value };

        if (field === 'itemtype') {
            const selectedItem = filteredItemTypes.find(it => it.itemtype === value);
            if (selectedItem) {
                const itemTypeId = selectedItem.itemid;
                const basePrice = selectedItem.baseprice || 0;
                const quantity = updatedItem.quantity || 1;

                // Find treatments for this item type
                const treatmentsForItem = treatments.filter(t => t.itemTypeId === itemTypeId);
                let defaultTreatment = '';
                let finalPrice = basePrice;

                if (treatmentsForItem.length > 0) {
                    const uniqueTreatments = treatmentsForItem.reduce((acc, curr) => {
                        if (!acc.find(item => item.treatment === curr.treatment)) {
                            acc.push(curr);
                        }
                        return acc;
                    }, []);

                    if (uniqueTreatments.length > 0) {
                        const firstTreatment = uniqueTreatments[0];
                        defaultTreatment = firstTreatment.treatment || '';
                        const multiplier = parseFloat(firstTreatment.priceMultiplier || 1.0);
                        finalPrice = basePrice * multiplier;
                    }
                }

                updatedItem.itemTypeId = itemTypeId;
                updatedItem.treatment = defaultTreatment;
                updatedItem.unitprice = parseFloat(finalPrice.toFixed(2));
                updatedItem.totalprice = parseFloat((finalPrice * quantity).toFixed(2));
                updatedItem.itemcategory = selectedItem.category || '';
            }
        }

        if (field === 'treatment') {
            const selectedItem = filteredItemTypes.find(it => it.itemtype === updatedItem.itemtype);
            if (selectedItem) {
                const itemTypeId = selectedItem.itemid;
                const basePrice = selectedItem.baseprice || 0;
                const quantity = updatedItem.quantity || 1;

                const treatmentObj = treatments.find(t => t.itemTypeId === itemTypeId && t.treatment === value);
                if (treatmentObj) {
                    const multiplier = parseFloat(treatmentObj.priceMultiplier || 1.0);
                    const finalPrice = basePrice * multiplier;
                    updatedItem.unitprice = parseFloat(finalPrice.toFixed(2));
                    updatedItem.totalprice = parseFloat((finalPrice * quantity).toFixed(2));
                }
            }
        }

        if (field === 'quantity') {
            const quantity = parseInt(value) || 1;
            const unitPrice = updatedItem.unitprice || 0;
            updatedItem.totalprice = parseFloat((unitPrice * quantity).toFixed(2));
            updatedItem.quantity = quantity;
        }

        setNewItem(updatedItem);
    };

    // Add item to the order
    const addItemToOrder = () => {
        // Validate
        if (!newItem.itemtype) {
            showError('Please select an item type');
            return;
        }
        if (!newItem.treatment) {
            showError('Please select a treatment');
            return;
        }
        if (!newItem.quantity || newItem.quantity < 1) {
            showError('Quantity must be at least 1');
            return;
        }

        // Add item to the list
        setItems(prevItems => [...prevItems, { ...newItem, id: Date.now() }]);

        // Reset new item form
        resetNewItemForm();

        showSuccess('Item added successfully!');
    };

    // Remove item from the list
    const removeItem = (index) => {
        const updatedItems = items.filter((_, i) => i !== index);
        setItems(updatedItems);
        showSuccess('Item removed successfully!');
    };

    // Reset new item form
    const resetNewItemForm = () => {
        if (filteredItemTypes.length > 0) {
            const firstItem = filteredItemTypes[0];
            const itemTypeId = firstItem.itemid;
            const basePrice = firstItem.baseprice || 0;

            const treatmentsForItem = treatments.filter(t => t.itemTypeId === itemTypeId);
            let defaultTreatment = '';
            let finalPrice = basePrice;

            if (treatmentsForItem.length > 0) {
                const uniqueTreatments = treatmentsForItem.reduce((acc, curr) => {
                    if (!acc.find(item => item.treatment === curr.treatment)) {
                        acc.push(curr);
                    }
                    return acc;
                }, []);

                if (uniqueTreatments.length > 0) {
                    const firstTreatment = uniqueTreatments[0];
                    defaultTreatment = firstTreatment.treatment || '';
                    const multiplier = parseFloat(firstTreatment.priceMultiplier || 1.0);
                    finalPrice = basePrice * multiplier;
                }
            }

            setNewItem({
                itemtype: firstItem.itemtype,
                itemTypeId: itemTypeId,
                quantity: 1,
                treatment: defaultTreatment,
                unitprice: parseFloat(finalPrice.toFixed(2)),
                totalprice: parseFloat(finalPrice.toFixed(2)),
                itemcategory: firstItem.category || '',
                instructions: ''
            });
        } else {
            setNewItem({
                itemtype: '',
                itemTypeId: null,
                quantity: 1,
                treatment: '',
                unitprice: 0,
                totalprice: 0,
                itemcategory: '',
                instructions: ''
            });
        }
    };

    // Submit additional items
    const submitAdditionalItems = async () => {
        if (items.length === 0) {
            showError('No items to add. Please add at least one item.');
            return;
        }

        if (!orderData) {
            showError('Order data not found. Please search for the order again.');
            return;
        }

        setSubmitting(true);

        try {
            const payload = {
                bookingId: orderData.Id || orderData.id || orderData.BookingId,
                additionalItems: items.map(item => ({
                    ItemType: item.itemtype,
                    ItemTypeId: item.itemTypeId,
                    Quantity: item.quantity,
                    Treatment: item.treatment,
                    UnitPrice: item.unitprice,
                    TotalPrice: item.totalprice,
                    ItemCategory: item.itemcategory || '',
                    Instructions: item.instructions || '',
                    IsAdditional: true
                })),
                notes: `Additional items added during pickup for order #${orderData.BookingReference || orderData.BookingId || orderData.id}`
            };

            console.log('Submitting additional items:', payload);

            const response = await axios.post(
                `${API_URL}bookings/add-items`,
                payload,
                {
                    headers: {
                        'Authorization': `Bearer ${auth.accessToken}`,
                        'Content-Type': 'application/json'
                    },
                    timeout: 15000
                }
            );

            if (response.data && response.data.success) {
                showSuccess('Additional items added successfully!');
                if (onSuccess) {
                    onSuccess(response.data);
                }
                // Reset form after success
                setTimeout(() => {
                    resetForm();
                    if (onHide) onHide();
                }, 2000);
            } else {
                throw new Error(response.data?.message || 'Failed to add items');
            }

        } catch (error) {
            console.error('Error adding items:', error);
            showError(error.response?.data?.message || 'Failed to add items. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    // Reset form
    const resetForm = () => {
        setSearchOrderId('');
        setOrderData(null);
        setItems([]);
        setSelectedShop(null);
        setFilteredItemTypes([]);
        setTreatments([]);
        resetNewItemForm();
        setErrors({});
    };

    // Close dialog
    const handleClose = () => {
        resetForm();
        if (onHide) onHide();
    };

    // Toast functions
    const showError = (message) => {
        if (toast.current) {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: message,
                life: 5000
            });
        }
    };

    const showSuccess = (message) => {
        if (toast.current) {
            toast.current.show({
                severity: 'success',
                summary: 'Success',
                detail: message,
                life: 4000
            });
        }
    };

    // Get treatment name for display
    const getTreatmentName = (treatment) => {
        const treat = treatments.find(t => t.treatment === treatment);
        return treat?.treatment || treatment || 'Standard';
    };

    // Get item type name for display
    const getItemTypeName = (itemType) => {
        const item = filteredItemTypes.find(it => it.itemtype === itemType);
        return item?.itemtype || itemType || 'Unknown';
    };

    // Get unique treatments for dropdown
    const getUniqueTreatments = (itemType) => {
        const selectedItem = filteredItemTypes.find(it => it.itemtype === itemType);
        if (!selectedItem) return [];

        const itemTypeId = selectedItem.itemid;
        const filtered = treatments.filter(t => t.itemTypeId === itemTypeId);

        const unique = filtered.reduce((acc, curr) => {
            if (!acc.find(item => item.treatment === curr.treatment)) {
                acc.push(curr);
            }
            return acc;
        }, []);

        return unique;
    };

    // Render order details
    const renderOrderDetails = () => {
        if (!orderData) return null;

        const bookingRef = orderData.BookingReference || orderData.bookingReference || orderData.Reference || orderData.reference || 'N/A';
        const customerName = orderData.CustomerName || orderData.customerName || orderData.UserName || orderData.userName || 'N/A';
        const customerPhone = orderData.CustomerPhone || orderData.customerPhone || orderData.Phone || orderData.phone || 'N/A';
        const status = orderData.Status || orderData.status || 'Pending';
        const totalAmount = orderData.TotalAmount || orderData.totalAmount || 0;
        const pickupDate = orderData.PickupDate || orderData.pickupDate || orderData.PickupDateTime || 'N/A';
        const deliveryDate = orderData.DeliveryDate || orderData.deliveryDate || orderData.DeliveryDateTime || 'N/A';

        return (
            <Card className="mt-3">
                <div className="grid">
                    <div className="col-12">
                        <h4>Order Details</h4>
                        <Divider />
                    </div>
                    <div className="col-12 md:col-6">
                        <p><strong>Order #:</strong> <Badge value={bookingRef} severity="info" /></p>
                        <p><strong>Customer:</strong> {customerName}</p>
                        <p><strong>Phone:</strong> {customerPhone}</p>
                        <p><strong>Status:</strong> <Tag value={status} severity={status === 'Completed' ? 'success' : status === 'Confirmed' ? 'info' : 'warning'} /></p>
                    </div>
                    <div className="col-12 md:col-6">
                        <p><strong>Total Amount:</strong> ₹{parseFloat(totalAmount).toFixed(2)}</p>
                        <p><strong>Pickup:</strong> {new Date(pickupDate).toLocaleString()}</p>
                        <p><strong>Delivery:</strong> {new Date(deliveryDate).toLocaleString()}</p>
                    </div>
                    {selectedShop && (
                        <div className="col-12">
                            <p><strong>Shop:</strong> {selectedShop.ShopName || selectedShop.name}</p>
                        </div>
                    )}
                </div>
            </Card>
        );
    };

    // Render existing items
    const renderExistingItems = () => {
        if (!items || items.length === 0) return null;

        return (
            <Card className="mt-3">
                <h4>Current Items in Order</h4>
                <DataTable value={items} size="small" className="p-datatable-sm">
                    <Column field="itemtype" header="Item" body={(rowData) => getItemTypeName(rowData.itemtype)} />
                    <Column field="quantity" header="Qty" />
                    <Column field="treatment" header="Treatment" body={(rowData) => getTreatmentName(rowData.treatment)} />
                    <Column field="unitprice" header="Unit Price" body={(rowData) => `₹${(rowData.unitprice || 0).toFixed(2)}`} />
                    <Column field="totalprice" header="Total" body={(rowData) => `₹${(rowData.totalprice || 0).toFixed(2)}`} />
                </DataTable>
                <div className="mt-2">
                    <strong>Total Items: </strong>
                    {items.reduce((sum, item) => sum + (item.quantity || 0), 0)}
                </div>
            </Card>
        );
    };

    return (
        <Dialog
            header="Add Additional Items to Order"
            visible={visible}
            style={{ width: '90%', maxWidth: '1200px' }}
            onHide={handleClose}
            closable={!submitting}
            dismissableMask={!submitting}
            modal
        >
            <Toast ref={toast} position="top-right" />

            {/* Search Section */}
            <Panel header="Search Order" toggleable>
                <div className="p-fluid">
                    <div className="grid">
                        <div className="col-12 md:col-8">
                            <div className="p-inputgroup">
                                <span className="p-inputgroup-addon">
                                    <i className="pi pi-search"></i>
                                </span>
                                <InputText
                                    placeholder="Enter Order ID (e.g., 12345)"
                                    value={searchOrderId}
                                    onChange={(e) => setSearchOrderId(e.target.value)}
                                    onKeyPress={handleKeyPress}
                                    disabled={searching || submitting}
                                />
                                <Button
                                    label="Search"
                                    icon={searching ? 'pi pi-spin pi-spinner' : 'pi pi-search'}
                                    onClick={searchOrder}
                                    disabled={searching || submitting || !searchOrderId.trim()}
                                    className="p-button-primary"
                                />
                            </div>
                        </div>
                        <div className="col-12 md:col-4">
                            <Button
                                label="Clear"
                                icon="pi pi-times"
                                className="p-button-outlined p-button-secondary"
                                onClick={resetForm}
                                disabled={searching || submitting}
                            />
                        </div>
                    </div>
                </div>
            </Panel>

            {/* Order Details */}
            {orderData && renderOrderDetails()}

            {/* Existing Items */}
            {orderData && renderExistingItems()}

            {/* Add Items Section */}
            {orderData && (
                <Panel header="Add New Items" toggleable collapsed={false} className="mt-3">
                    {filteredItemTypes.length === 0 && (
                        <Message severity="info" text="Loading item types..." className="mb-3" />
                    )}

                    <div className="grid">
                        {/* Item Type Dropdown */}
                        <div className="col-12 md:col-3">
                            <div className="field">
                                <label className="block mb-2 font-medium">
                                    Item Type <span className="text-red-500">*</span>
                                </label>
                                <Dropdown
                                    value={newItem.itemtype}
                                    options={filteredItemTypes.map(item => ({
                                        label: item.itemtype,
                                        value: item.itemtype,
                                        itemid: item.itemid,
                                        baseprice: item.baseprice,
                                        category: item.category
                                    }))}
                                    optionLabel="label"
                                    optionValue="value"
                                    onChange={(e) => handleNewItemChange('itemtype', e.value)}
                                    placeholder="Select Item"
                                    className="w-full"
                                    filter
                                    disabled={filteredItemTypes.length === 0 || submitting}
                                />
                            </div>
                        </div>

                        {/* Quantity */}
                        <div className="col-12 md:col-2">
                            <div className="field">
                                <label className="block mb-2 font-medium">
                                    Quantity <span className="text-red-500">*</span>
                                </label>
                                <div className="flex items-center gap-2">
                                    <Button
                                        icon="pi pi-minus"
                                        onClick={() => {
                                            const newQty = Math.max(1, (newItem.quantity || 1) - 1);
                                            handleNewItemChange('quantity', newQty);
                                        }}
                                        disabled={newItem.quantity <= 1 || submitting}
                                        size="small"
                                        text
                                        rounded
                                    />
                                    <InputText
                                        type="number"
                                        value={newItem.quantity}
                                        onChange={(e) => {
                                            const val = parseInt(e.target.value) || 1;
                                            handleNewItemChange('quantity', val);
                                        }}
                                        className="w-full text-center"
                                        min="1"
                                        disabled={submitting}
                                    />
                                    <Button
                                        icon="pi pi-plus"
                                        onClick={() => {
                                            const newQty = (newItem.quantity || 1) + 1;
                                            handleNewItemChange('quantity', newQty);
                                        }}
                                        disabled={submitting}
                                        size="small"
                                        text
                                        rounded
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Treatment Dropdown */}
                        <div className="col-12 md:col-3">
                            <div className="field">
                                <label className="block mb-2 font-medium">
                                    Treatment <span className="text-red-500">*</span>
                                </label>
                                <Dropdown
                                    value={newItem.treatment}
                                    options={getUniqueTreatments(newItem.itemtype).map(t => ({
                                        label: t.treatment,
                                        value: t.treatment,
                                        priceMultiplier: t.priceMultiplier
                                    }))}
                                    optionLabel="label"
                                    optionValue="value"
                                    onChange={(e) => handleNewItemChange('treatment', e.value)}
                                    placeholder={!newItem.itemtype ? "Select item first" :
                                        isLoadingTreatments ? "Loading..." :
                                        "Select Treatment"}
                                    className="w-full"
                                    disabled={!newItem.itemtype || isLoadingTreatments || submitting || getUniqueTreatments(newItem.itemtype).length === 0}
                                />
                                {newItem.itemtype && getUniqueTreatments(newItem.itemtype).length === 0 && !isLoadingTreatments && (
                                    <small className="p-warn block mt-1">No treatments available for this item</small>
                                )}
                            </div>
                        </div>

                        {/* Price Display */}
                        <div className="col-12 md:col-2">
                            <div className="field">
                                <label className="block mb-2 font-medium">Unit Price</label>
                                <div className="p-2 surface-50 border-round text-center">
                                    <span className="font-bold text-lg text-primary">
                                        ₹{(newItem.unitprice || 0).toFixed(2)}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="col-12 md:col-2">
                            <div className="field">
                                <label className="block mb-2 font-medium">Total Price</label>
                                <div className="p-2 surface-50 border-round text-center">
                                    <span className="font-bold text-lg text-primary">
                                        ₹{(newItem.totalprice || 0).toFixed(2)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Instructions */}
                    <div className="grid">
                        <div className="col-12">
                            <div className="field">
                                <label className="block mb-2 font-medium">Special Instructions</label>
                                <InputTextarea
                                    value={newItem.instructions}
                                    onChange={(e) => handleNewItemChange('instructions', e.target.value)}
                                    rows={2}
                                    className="w-full"
                                    placeholder="Any special instructions for this item..."
                                    disabled={submitting}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-content-end gap-2 mt-3">
                        <Button
                            label="Add Item"
                            icon="pi pi-plus"
                            className="p-button-success"
                            onClick={addItemToOrder}
                            disabled={!newItem.itemtype || !newItem.treatment || submitting || isLoadingTreatments}
                        />
                    </div>
                </Panel>
            )}

            {/* Added Items List */}
            {orderData && items.filter(item => item.id && item.id > 999999).length > 0 && (
                <Card className="mt-3">
                    <div className="flex justify-content-between align-items-center mb-3">
                        <h4>Items to Add ({items.filter(item => item.id && item.id > 999999).length})</h4>
                    </div>
                    <DataTable
                        value={items.filter(item => item.id && item.id > 999999)}
                        size="small"
                        className="p-datatable-sm"
                    >
                        <Column field="itemtype" header="Item" body={(rowData) => getItemTypeName(rowData.itemtype)} />
                        <Column field="quantity" header="Qty" />
                        <Column field="treatment" header="Treatment" body={(rowData) => getTreatmentName(rowData.treatment)} />
                        <Column field="unitprice" header="Unit Price" body={(rowData) => `₹${(rowData.unitprice || 0).toFixed(2)}`} />
                        <Column field="totalprice" header="Total" body={(rowData) => `₹${(rowData.totalprice || 0).toFixed(2)}`} />
                        <Column
                            header="Action"
                            body={(rowData, { rowIndex }) => (
                                <Button
                                    icon="pi pi-trash"
                                    className="p-button-danger p-button-text p-button-sm"
                                    onClick={() => removeItem(rowIndex)}
                                    disabled={submitting}
                                    tooltip="Remove item"
                                />
                            )}
                        />
                    </DataTable>

                    <div className="mt-3 flex justify-content-between align-items-center">
                        <div>
                            <strong>Total Items: </strong>
                            {items.filter(item => item.id && item.id > 999999).reduce((sum, item) => sum + (item.quantity || 0), 0)}
                        </div>
                        <div>
                            <strong>Total Amount: </strong>
                            <span className="text-xl font-bold text-primary">
                                ₹{items.filter(item => item.id && item.id > 999999).reduce((sum, item) => sum + (item.totalprice || 0), 0).toFixed(2)}
                            </span>
                        </div>
                    </div>
                </Card>
            )}

            {/* Action Buttons */}
            {orderData && (
                <div className="flex justify-content-between mt-4">
                    <Button
                        label="Cancel"
                        icon="pi pi-times"
                        className="p-button-secondary"
                        onClick={handleClose}
                        disabled={submitting}
                    />
                    <Button
                        label={submitting ? 'Adding Items...' : 'Add Items to Order'}
                        icon={submitting ? 'pi pi-spin pi-spinner' : 'pi pi-check'}
                        className="p-button-success"
                        onClick={submitAdditionalItems}
                        disabled={submitting || items.filter(item => item.id && item.id > 999999).length === 0}
                        loading={submitting}
                    />
                </div>
            )}
        </Dialog>
    );
};

export default AdditionalItemsComponent;
